import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import axiosInstance from "../utils/api";
import {
  MdBusiness,
  MdOutlineConfirmationNumber,
  MdOutlineMeetingRoom,
  MdStraighten,
  MdCategory,
  MdPalette,
  MdWifi,
  MdBolt,
  MdWarning,
  MdCheckCircle,
  MdMoreHoriz,
  MdRadioButtonUnchecked,
  MdAccessTime,
  MdAssignment,
  MdSpeed,
  MdTrendingDown,
  MdUpdate,
} from "react-icons/md";
import toast from "react-hot-toast";
import { TbMapCog } from "react-icons/tb";

const PlanViewer = ({ file, onClose }) => {
  if (!file) return null;

  const isImage = /\.(png|jpe?g|webp|gif)$/i.test(file);
  const isPdf = /\.pdf$/i.test(file);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center">
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white text-3xl font-bold hover:scale-110 transition"
      >
        ×
      </button>

      <div className="max-w-[90vw] max-h-[90vh] bg-white rounded-xl overflow-hidden shadow-2xl">
        {isImage && (
          <img
            src={file}
            alt="Stand plan"
            className="max-w-[90vw] max-h-[90vh] object-contain"
          />
        )}

        {isPdf && (
          <iframe
            src={file}
            title="Plan PDF"
            className="w-[90vw] h-[90vh]"
          />
        )}
      </div>
    </div>
  );
};

const StandDetailsModal = ({ isOpen, onClose, stand, isSubAdmin }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [electricity, setElectricity] = useState({
    amperage: "",
    linesCount: "",
    phase: null,
    need24h: false,
    comment: "",
  });
  const [internet, setInternet] = useState({
    needed: false,
    type: null,
    speedMbps: "",
    rj45Count: "",
    comment: "",
  });
  const [eventSettings, setEventSettings] = useState({
    electricity: true,
    internet: true,
  });
  const [plan, setPlan] = useState(null);
  const [uploadingPlan, setUploadingPlan] = useState(false);
  const [showPlanViewer, setShowPlanViewer] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [onsiteData, setOnsiteData] = useState(null);
  const [editingOnsite, setEditingOnsite] = useState(false);
  const [savingOnsite, setSavingOnsite] = useState(false);
  const [onsiteForm, setOnsiteForm] = useState({
    arrivalDate: "",
    contractor: {
      companyName: "",
      chiefName: "",
      phone: "",
      email: "",
    },
  });

  useEffect(() => {
    const fetchEventSettings = async () => {
      try {
        const res = await axiosInstance.get(`/event/${stand.event}`);
        const ev = res.data.event;

        setEventSettings({
          electricity: ev?.electricity ?? true,
          internet: ev?.internet ?? true,
        });
      } catch (err) {
        console.error("Failed to load event settings", err);
      }
    };

    if (isOpen && stand?.event) {
      fetchEventSettings();
    }
  }, [isOpen, stand]);

  useEffect(() => {
    if (isOpen && stand?._id) {
      fetchActivities();
    }
  }, [isOpen, stand?._id]);

  useEffect(() => {
    if (stand?.plan) {
      setPlan(stand.plan);
    }
  }, [stand]);

  useEffect(() => {
    if (stand) {
      setElectricity(stand.electricity || {
        amperage: "",
        linesCount: "",
        phase: null,
        need24h: false,
        comment: "",
      });
      setInternet(stand.internet || {
        needed: false,
        type: null,
        speedMbps: "",
        rj45Count: "",
        comment: "",
      });
    }
  }, [stand]);

  useEffect(() => {
    if (stand?.onsite) {
      setOnsiteData(stand.onsite);
      setOnsiteForm({
        arrivalDate: stand.onsite.arrivalDate
          ? new Date(stand.onsite.arrivalDate).toISOString().slice(0, 16)
          : "",
        contractor: {
          companyName: stand.onsite.contractor?.companyName || "",
          chiefName: stand.onsite.contractor?.chiefName || "",
          phone: stand.onsite.contractor?.phone || "",
          email: stand.onsite.contractor?.email || "",
        },
      });
    }
  }, [stand]);

  const handleSaveOnsite = async () => {
    setSavingOnsite(true);
    try {
      await axiosInstance.put(`/stand/${stand._id}`, {
        onsite: {
          arrivalDate: onsiteForm.arrivalDate || null,
          contractor: onsiteForm.contractor,
        },
      });

      setOnsiteData({
        arrivalDate: onsiteForm.arrivalDate || null,
        contractor: { ...onsiteForm.contractor },
      });

      toast.success("Onsite info saved successfully!");
      setEditingOnsite(false);
    } catch (error) {
      console.error("Failed to save onsite info", error);
      toast.error("Error while saving onsite info. Please try again.");
    } finally {
      setSavingOnsite(false);
    }
  };

  const handleDeletePlanFile = async (fileUrl) => {
    if (!window.confirm("Are you sure you want to delete this plan file?")) return;

    try {
      const { data } = await axiosInstance.delete(`/stand/plan/${stand._id}`, {
        data: { fileUrl },
      });

      setPlan(data.stand.plan);
      toast.success("Plan file deleted successfully");
    } catch (err) {
      toast.error("Failed to delete plan file");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/stand/${stand._id}`, {
        electricity: {
          ...electricity,
          amperage: Number(electricity.amperage) || 0,
          linesCount: Number(electricity.linesCount) || 0,
          phase: electricity.phase || null,
        },
        internet: {
          ...internet,
          speedMbps: Number(internet.speedMbps) || 0,
          rj45Count: Number(internet.rj45Count) || 0,
          type: internet.type || null,
        },
      });

      toast.success("Internet and electricity settings saved successfully!");
      onClose();
    } catch (error) {
      console.error("Failed to save stand", error);
      toast.error("Error while saving. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const fetchActivities = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(`/activity/stand/${stand._id}`);

      if (response.data.success) {
        setActivities(response.data.activities || []);
      } else {
        setError(response.data.message || "Failed to fetch activities");
      }
    } catch (err) {
      setError("Error fetching activities");
      console.error("Error fetching activities:", err);
    } finally {
      setLoading(false);
    }
  };

  if (!stand) return null;

  const handlePlanUpload = async (files) => {
    if (!files || files.length === 0) return;

    const formData = new FormData();

    for (let i = 0; i < files.length; i++) {
      formData.append("plans", files[i]); // IMPORTANT: must match backend
    }

    try {
      setUploadingPlan(true);

      const { data } = await axiosInstance.post(
        `/stand/plan/${stand._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setPlan(data.stand.plan);

      toast.success(
        data.wasValidated
          ? "Plans uploaded. Status reset to Submitted."
          : "Plans uploaded successfully"
      );
    } catch (err) {
      toast.error("Failed to upload plans");
    } finally {
      setUploadingPlan(false);
    }
  };

  const handlePlanStatusChange = async (newStatus) => {
    try {
      await axiosInstance.put(`/stand/${stand._id}`, {
        plan: {
          ...plan,
          status: newStatus,
        },
      });

      setPlan((prev) => ({
        ...prev,
        status: newStatus,
      }));

      toast.success("Plan status updated");
    } catch (err) {
      toast.error("Failed to update plan status");
    }
  };

  const formatFieldName = (key) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const calculateActivityStatus = (activity) => {
    const { startAt, endAt, markedAt, status, markedBy } = activity;

    if (!startAt || !endAt) {
      return {
        status: status || "pending",
        delayDays: 0,
        earlyDays: 0,
        message: null,
      };
    }

    const getDateOnly = (date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const startDate = getDateOnly(startAt);
    const endDate = getDateOnly(endAt);
    const now = getDateOnly(new Date());

    if (!markedAt) {
      if (now > endDate) {
        const delayDays = Math.floor((now - endDate) / (1000 * 60 * 60 * 24));
        return {
          status: "delayed",
          delayDays,
          earlyDays: 0,
          message: `Overdue by ${delayDays} day${delayDays > 1 ? "s" : ""}`,
        };
      }

      if (now >= startDate) {
        return {
          status: "pending",
          delayDays: 0,
          earlyDays: 0,
          message: "In progress",
        };
      }

      return {
        status: "pending",
        delayDays: 0,
        earlyDays: 0,
        message: "Not started yet",
      };
    }

    const markedDate = getDateOnly(markedAt);

    if (markedDate > endDate) {
      const delayDays = Math.floor((markedDate - endDate) / (1000 * 60 * 60 * 24));

      return {
        status: "done",
        delayDays,
        earlyDays: 0,
        message: `Completed with delay of ${delayDays} day${delayDays > 1 ? "s" : ""}`,
      };
    }

    if (markedDate < startDate) {
      const earlyDays = Math.floor((startDate - markedDate) / (1000 * 60 * 60 * 24));

      return {
        status: "done",
        delayDays: 0,
        earlyDays,
        message: `Completed ${earlyDays} day${earlyDays > 1 ? "s" : ""} early`,
      };
    }

    return {
      status: "done",
      delayDays: 0,
      earlyDays: 0,
      message: "Completed on time",
    };
  };

  const getStatusStyle = (status) => {
    const styles = {
      done: "bg-emerald-500 text-white shadow-lg shadow-emerald-200",
      delayed: "bg-rose-500 text-white shadow-lg shadow-rose-200",
      pending: "bg-amber-500 text-white shadow-lg shadow-amber-200",
    };
    return styles[status] || "bg-slate-300 text-slate-600 shadow-lg shadow-slate-100";
  };

  const getStatusIcon = (status) => {
    if (status === "done") return <MdCheckCircle />;
    if (status === "delayed") return <MdWarning />;
    if (status === "pending") return <MdMoreHoriz />;
    return <MdRadioButtonUnchecked />;
  };

  const PLAN_STATUS = {
    not_submitted: { label: "Not submitted", color: "bg-slate-200 text-slate-700" },
    submitted: { label: "Submitted", color: "bg-blue-100 text-blue-700" },
    to_correct: { label: "To correct", color: "bg-amber-100 text-amber-700" },
    validated: { label: "Validated", color: "bg-emerald-100 text-emerald-700" },
  };

  const fieldIcons = {
    standNumber: <MdOutlineConfirmationNumber />,
    tentNumber: <MdBusiness />,
    hall: <MdOutlineMeetingRoom />,
    size: <MdStraighten />,
    type: <MdCategory />,
    design: <MdPalette />,
  };

  const renderTimestamp = (value) => {
    if (!value) return <span className="text-slate-400 italic">Not set</span>;
    return (
      <span className="text-slate-600">
        {new Date(value).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </span>
    );
  };

  const renderDate = (value) => {
    if (!value) return <span className="text-slate-400 italic">Not set</span>;
    return (
      <span className="text-slate-600 font-medium">
        {new Date(value).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })}
      </span>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Stand Details" size="xl">
      <div className="min-h-full">

        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200"></div>
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
            </div>
            <span className="mt-4 text-slate-600 font-medium">Loading activities...</span>
          </div>
        )}

        {error && (
          <div className="m-6">
            <div className="p-4 bg-rose-50 border-l-4 border-rose-500 rounded-lg shadow-sm">
              <div className="flex items-center">
                <span className="text-rose-500 text-xl mr-3">⚠</span>
                <p className="text-rose-700 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <div className="p-6 space-y-6">
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-1">
                    {stand.exhibitorName}
                  </h3>
                  <p className="text-slate-500">Stand #{stand.standNumber}</p>
                </div>
                {stand.progress !== undefined && (
                  <div className="text-right">
                    <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                      {stand.progress.toFixed(2)}%
                    </div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Complete</div>
                  </div>
                )}
              </div>

              {stand.progress !== undefined && (
                <div className="mb-6">
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden shadow-inner">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${stand.progress >= 80
                        ? "bg-gradient-to-r from-emerald-500 to-emerald-600"
                        : stand.progress >= 50
                          ? "bg-gradient-to-r from-blue-500 to-blue-600"
                          : stand.progress >= 25
                            ? "bg-gradient-to-r from-amber-500 to-amber-600"
                            : "bg-gradient-to-r from-rose-500 to-rose-600"
                        }`}
                      style={{ width: `${stand.progress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {Object.keys(fieldIcons).map((key) => {
                  if (!stand[key]) return null;

                  return (
                    <div
                      key={key}
                      className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-4 border border-slate-200 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg text-slate-600">
                          {fieldIcons[key]}
                        </span>
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          {formatFieldName(key)}
                        </span>
                      </div>
                      <span className="text-slate-900 font-bold text-sm">
                        {String(stand[key])}
                      </span>
                    </div>
                  );
                })}

                {stand.customFields &&
                  typeof stand.customFields === "object" &&
                  Object.entries(stand.customFields).map(([label, value]) => (
                    <div
                      key={label}
                      className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-4 border border-slate-200 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg text-slate-600">
                          <MdAssignment />
                        </span>
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          {label}
                        </span>
                      </div>
                      <span className="text-slate-900 font-bold text-sm">
                        {String(value)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>

            {(stand.onsite || onsiteData) && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <MdAccessTime /> Arrival On Site
                  </h3>
                  {!isSubAdmin && (
                    <button
                      onClick={() => setEditingOnsite((prev) => !prev)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 transition"
                    >
                      {editingOnsite ? "Cancel" : "Edit"}
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                      Arrival Date
                    </span>
                    {editingOnsite ? (
                      <input
                        type="datetime-local"
                        value={onsiteForm.arrivalDate}
                        onChange={(e) =>
                          setOnsiteForm({ ...onsiteForm, arrivalDate: e.target.value })
                        }
                        className="input w-full"
                      />
                    ) : (
                      <span className="text-slate-900 font-semibold">
                        {onsiteData?.arrivalDate
                          ? new Date(onsiteData.arrivalDate).toLocaleString()
                          : "Not set"}
                      </span>
                    )}
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                      Contractor
                    </span>
                    {editingOnsite ? (
                      <input
                        type="text"
                        value={onsiteForm.contractor.companyName}
                        onChange={(e) =>
                          setOnsiteForm({
                            ...onsiteForm,
                            contractor: { ...onsiteForm.contractor, companyName: e.target.value },
                          })
                        }
                        placeholder="Company name"
                        className="input w-full"
                      />
                    ) : (
                      <span className="text-slate-900 font-semibold">
                        {onsiteData?.contractor?.companyName || "Not provided"}
                      </span>
                    )}
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                      Contractor's Chief
                    </span>
                    {editingOnsite ? (
                      <input
                        type="text"
                        value={onsiteForm.contractor.chiefName}
                        onChange={(e) =>
                          setOnsiteForm({
                            ...onsiteForm,
                            contractor: { ...onsiteForm.contractor, chiefName: e.target.value },
                          })
                        }
                        placeholder="Chief name"
                        className="input w-full"
                      />
                    ) : (
                      <span className="text-slate-900 font-semibold">
                        {onsiteData?.contractor?.chiefName || "Not provided"}
                      </span>
                    )}
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                      Phone Number
                    </span>
                    {editingOnsite ? (
                      <input
                        type="tel"
                        value={onsiteForm.contractor.phone}
                        onChange={(e) =>
                          setOnsiteForm({
                            ...onsiteForm,
                            contractor: { ...onsiteForm.contractor, phone: e.target.value },
                          })
                        }
                        placeholder="Phone number"
                        className="input w-full"
                      />
                    ) : (
                      <span className="text-slate-900 font-semibold">
                        {onsiteData?.contractor?.phone || "Not provided"}
                      </span>
                    )}
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 md:col-span-2">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                      Email
                    </span>
                    {editingOnsite ? (
                      <input
                        type="email"
                        value={onsiteForm.contractor.email}
                        onChange={(e) =>
                          setOnsiteForm({
                            ...onsiteForm,
                            contractor: { ...onsiteForm.contractor, email: e.target.value },
                          })
                        }
                        placeholder="Email address"
                        className="input w-full"
                      />
                    ) : (
                      <span className="text-slate-900 font-semibold">
                        {onsiteData?.contractor?.email || "Not provided"}
                      </span>
                    )}
                  </div>
                </div>

                {editingOnsite && (
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={handleSaveOnsite}
                      disabled={savingOnsite}
                      className="px-6 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {savingOnsite ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <MdCheckCircle />
                          Save Onsite Info
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <TbMapCog /> Stand Plan
                </h3>

                {!isSubAdmin && <select
                  value={plan?.status}
                  onChange={(e) => handlePlanStatusChange(e.target.value)}
                  className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide border outline-none cursor-pointer
  ${PLAN_STATUS[plan?.status || "not_submitted"].color}`}
                >
                  {Object.entries(PLAN_STATUS).map(([key, val]) => (
                    <option
                      key={key}
                      value={key}
                      disabled={key === "validated" && (!plan?.files || plan.files.length === 0)}
                    >
                      {val.label}
                    </option>
                  ))}
                </select>}
              </div>

              {plan?.files && plan.files.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {plan.files.map((file, index) => (
                    <div
                      key={index}
                      className="group cursor-pointer relative rounded-xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl transition"
                    >
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition z-10" />

                      {/* View label */}
                      <div
                        onClick={() => { setShowPlanViewer(true); setSelectedFile(file); }}
                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition z-20"
                      >
                        <span className="px-4 py-2 bg-white rounded-lg font-bold text-sm shadow-lg">
                          View
                        </span>
                      </div>

                      {/* Delete button — only for non-subadmin */}
                      {!isSubAdmin && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeletePlanFile(file); }}
                          className="absolute top-2 right-2 z-30 opacity-0 group-hover:opacity-100 transition bg-rose-600 hover:bg-rose-700 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg"
                          title="Delete this file"
                        >
                          ×
                        </button>
                      )}

                      {/\.pdf$/i.test(file) ? (
                        <iframe
                          src={`${file}#page=1&toolbar=0`}
                          title="Plan PDF Preview"
                          className="w-full h-40 pointer-events-none bg-slate-100"
                        />
                      ) : (
                        <img
                          src={file}
                          alt="Plan preview"
                          className="w-full h-40 object-cover"
                        />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500">
                  No plan uploaded yet
                </div>
              )}

              <div className="mt-4 flex items-center justify-between">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="file"
                    accept=".pdf,image/*"
                    multiple
                    className="hidden"
                    disabled={uploadingPlan}
                    onChange={(e) => handlePlanUpload(e.target.files)}
                  />

                  <span
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-semibold transitio ${uploadingPlan
                      ? "bg-slate-200 border-slate-300 text-slate-500 cursor-not-allowed"
                      : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400"}`}
                  >
                    {uploadingPlan ? (
                      <>
                        <span className="w-4 h-4 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
                        Uploading…
                      </>
                    ) : (
                      <>
                        <TbMapCog className="text-base text-slate-600" />
                        Upload plan
                      </>
                    )}
                  </span>
                </label>

                {stand.plan?.uploadedAt && (
                  <span className="text-xs text-slate-500">
                    Uploaded on{" "}
                    {new Date(stand.plan.uploadedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 bg-white rounded-2xl shadow-xl p-2 border border-slate-200">
              {eventSettings.electricity && (
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <MdBolt /> Electricity
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="label">Requested amperage (A)</label>
                      <input
                        type="number"
                        value={electricity.amperage}
                        onChange={(e) =>
                          setElectricity({ ...electricity, amperage: e.target.value })
                        }
                        className="input"
                        disabled={isSubAdmin}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="label">Number of lines / circuits</label>
                      <input
                        type="number"
                        value={electricity.linesCount}
                        onChange={(e) =>
                          setElectricity({ ...electricity, linesCount: e.target.value })
                        }
                        className="input"
                        disabled={isSubAdmin}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="label">Phase</label>
                      <select
                        value={electricity.phase}
                        onChange={(e) =>
                          setElectricity({ ...electricity, phase: e.target.value })
                        }
                        className="input"
                        disabled={isSubAdmin}
                      >
                        <option value="">Select phase</option>
                        <option value="monophase">Single-phase</option>
                        <option value="triphase">Three-phase</option>
                      </select>
                    </div>

                    <div className="flex items-end">
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <input
                          className="scale-125 accent-blue-800"
                          type="checkbox" disabled={isSubAdmin} checked={electricity.need24h}
                          onChange={(e) => setElectricity({ ...electricity, need24h: e.target.checked })}
                        /> 24/7 power required </label>
                    </div>

                    <div className="col-span-2 space-y-2">
                      <label className="label">Electricity comments</label>
                      <textarea
                        value={electricity.comment}
                        onChange={(e) =>
                          setElectricity({ ...electricity, comment: e.target.value })
                        }
                        className="input"
                        disabled={isSubAdmin}
                      />
                    </div>
                  </div>
                </div>
              )}

              {eventSettings.internet && (
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <MdWifi /> Internet
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                        <input
                          className="scale-125 accent-blue-800"
                          type="checkbox"
                          checked={internet.needed}
                          onChange={(e) =>
                            setInternet({ ...internet, needed: e.target.checked })
                          }
                          disabled={isSubAdmin}
                        />
                        Internet required
                      </label>
                    </div>

                    {internet.needed && (
                      <>
                        <div className="space-y-2">
                          <label className="label">Connection type</label>
                          <select
                            value={internet.type}
                            onChange={(e) =>
                              setInternet({ ...internet, type: e.target.value })
                            }
                            className="input"
                            disabled={isSubAdmin}
                          >
                            <option value="">Select type</option>
                            <option value="wifi">WiFi</option>
                            <option value="wired">Wired</option>
                            <option value="both">Both</option>
                          </select>
                        </div>

                        <div className="space-y-2">
                          <label className="label">Requested speed (Mbps)</label>
                          <input
                            type="number"
                            value={internet.speedMbps}
                            onChange={(e) =>
                              setInternet({ ...internet, speedMbps: e.target.value })
                            }
                            className="input"
                            disabled={isSubAdmin}
                          />
                        </div>

                        {internet.type !== "wifi" && (
                          <div className="space-y-2">
                            <label className="label">Number of RJ45 ports</label>
                            <input
                              type="number"
                              value={internet.rj45Count}
                              onChange={(e) =>
                                setInternet({ ...internet, rj45Count: e.target.value })
                              }
                              className="input"
                              disabled={isSubAdmin}
                            />
                          </div>
                        )}

                        <div className="col-span-2 space-y-2">
                          <label className="label">Internet comments</label>
                          <textarea
                            value={internet.comment}
                            onChange={(e) =>
                              setInternet({ ...internet, comment: e.target.value })
                            }
                            className="input"
                            disabled={isSubAdmin}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}


              {(eventSettings.internet || eventSettings.electricity) && !isSubAdmin && (
                <div className="flex justify-center gap-3 pt-4 border-slate-200">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-6 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <MdCheckCircle />
                        Save
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>


            {activities.length > 0 && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                    Stand Progress Activities
                  </h3>
                  <div className="text-sm text-slate-500 bg-slate-100 px-4 py-2 rounded-full font-medium">
                    {activities.filter(a => calculateActivityStatus(a).status === 'done').length} / {activities.length} Complete
                  </div>
                </div>

                <div className="space-y-3">
                  {activities.map((activity, index) => {
                    const statusInfo = calculateActivityStatus(activity);

                    return (
                      <div
                        key={activity._id}
                        className="group relative"
                      >

                        {index < activities.length - 1 && (
                          <div className="absolute left-6 top-20 w-0.5 h-6 bg-gradient-to-b from-slate-300 to-transparent" />
                        )}

                        <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-all duration-300 hover:scale-[1.01]">
                          <div className="flex items-start gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold shadow-lg transition-transform group-hover:scale-110 flex-shrink-0 ${getStatusStyle(statusInfo.status)}`}>
                              {getStatusIcon(statusInfo.status)}
                            </div>

                            <div className="flex-1 min-w-0">
                              <h4 className="text-base font-bold text-slate-900 mb-2">
                                {activity.name}
                              </h4>

                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div className="bg-blue-50 rounded-lg p-2 border border-blue-100">
                                  <div className="text-xs text-blue-600 font-semibold mb-1">Start Date</div>
                                  <div className="text-xs">{renderDate(activity.startAt)}</div>
                                </div>
                                <div className="bg-purple-50 rounded-lg p-2 border border-purple-100">
                                  <div className="text-xs text-purple-600 font-semibold mb-1">End Date</div>
                                  <div className="text-xs">{renderDate(activity.endAt)}</div>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
                                <MdAccessTime className="w-3 h-3" />
                                <span className="font-medium">Marked At:</span>
                                {renderTimestamp(activity.markedAt)}

                                {activity.markedBy && (
                                  <div className="text-xs text-slate-500 italic">
                                    - By <span className="font-semibold">{activity.markedBy.name}</span>
                                  </div>
                                )}
                              </div>

                              {statusInfo.message && (
                                <div
                                  className={`flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-lg inline-flex ${statusInfo.delayDays > 0
                                    ? "bg-rose-50 text-rose-700 border border-rose-200"
                                    : statusInfo.status === "done" && statusInfo.earlyDays > 0
                                      ? "bg-blue-50 text-blue-700 border border-blue-200"
                                      : statusInfo.status === "done"
                                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                        : statusInfo.status === "delayed"
                                          ? "bg-rose-50 text-rose-700 border border-rose-200"
                                          : "bg-amber-50 text-amber-700 border border-amber-200"
                                    }`}>
                                  {statusInfo.delayDays > 0 && <MdTrendingDown className="w-4 h-4" />}
                                  {statusInfo.status === "done" && statusInfo.earlyDays > 0 && (
                                    <MdSpeed className="w-4 h-4" />
                                  )}

                                  {statusInfo.message}
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-4 flex-shrink-0">

                              <div className="relative w-16 h-16">
                                <svg className="w-16 h-16 transform -rotate-90">
                                  <circle
                                    cx="32"
                                    cy="32"
                                    r="28"
                                    stroke="currentColor"
                                    strokeWidth="6"
                                    fill="none"
                                    className="text-slate-200"
                                  />
                                  <circle
                                    cx="32"
                                    cy="32"
                                    r="28"
                                    stroke="currentColor"
                                    strokeWidth="6"
                                    fill="none"
                                    strokeDasharray={`${2 * Math.PI * 28}`}
                                    strokeDashoffset={`${2 * Math.PI * 28 * (1 - activity.percent / 100)}`}
                                    className={`transition-all duration-500 ${statusInfo.status === 'done' ? 'text-emerald-500' :
                                      statusInfo.status === 'pending' ? 'text-amber-500' :
                                        statusInfo.status === 'delayed' ? 'text-rose-500' :
                                          'text-slate-400'
                                      }`}
                                    strokeLinecap="round"
                                  />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-sm font-bold text-slate-900">
                                    {activity.percent}%
                                  </span>
                                </div>
                              </div>

                              <div className={`px-4 py-2 rounded-xl font-bold text-sm uppercase tracking-wider ${getStatusStyle(statusInfo.status)}`}>
                                {statusInfo.status}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!loading && activities.length === 0 && !error && (
              <div className="bg-white rounded-2xl shadow-xl p-12 border border-slate-200 text-center">
                <div className="text-6xl mb-4 text-slate-400 flex justify-center">
                  <MdAssignment />
                </div>
                <p className="text-slate-500 text-lg font-medium">No activities found for this stand</p>
                <p className="text-slate-400 text-sm mt-2">Activities will appear here once they are created</p>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <MdUpdate /> Daily Updates
              </h3>

              {stand.dailyUpdates && stand.dailyUpdates.length > 0 ? (
                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                  {stand.dailyUpdates
                    .slice()
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((update) => (
                      <div
                        key={update._id}
                        className="bg-slate-50 rounded-xl p-4 border border-slate-200"
                      >
                        {/* Header */}
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-xs text-slate-500">
                            {new Date(update.date).toLocaleString()}
                          </span>

                          {update.addedBy && (
                            <span className="text-xs text-slate-400">
                              By {update.addedBy.name || "User"}
                            </span>
                          )}
                        </div>

                        {/* Comment */}
                        {update.comment && (
                          <p className="text-sm text-slate-800 mb-3 whitespace-pre-line">
                            {update.comment}
                          </p>
                        )}

                        {/* Photos */}
                        {update.photos && update.photos.length > 0 && (
                          <div className="flex gap-2 flex-wrap">
                            {update.photos.map((photo, idx) => (
                              <img
                                key={idx}
                                src={photo}
                                alt="Daily update"
                                className="w-24 h-24 object-cover rounded-lg border hover:scale-105 transition cursor-pointer"
                                onClick={() => window.open(photo, "_blank")}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm italic">
                  No daily updates available
                </p>
              )}
            </div>

            {(stand.createdAt || stand.updatedAt) && (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-slate-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <MdAccessTime size={22} />
                  Timestamps
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {stand.createdAt && (
                    <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-4 border border-slate-200">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">
                        Created At
                      </span>
                      <span className="text-slate-900 font-medium">
                        {renderTimestamp(stand.createdAt)}
                      </span>
                    </div>
                  )}
                  {stand.updatedAt && (
                    <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-4 border border-slate-200">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">
                        Last Updated
                      </span>
                      <span className="text-slate-900 font-medium">
                        {renderTimestamp(stand.updatedAt)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {showPlanViewer && (
        <PlanViewer
          file={selectedFile}
          onClose={() => {
            setShowPlanViewer(false);
            setSelectedFile(null);
          }}
        />
      )}
    </Modal>
  );
};

export default StandDetailsModal;